import DialogContainer from "../../components/common/DialogContainer";
import { setSocket, fetchAppointmentUnreadCount, fetchMessagesUnreadCount, setMessagesUnreadCount, incrementMessagesUnreadCount } from "../../redux/user-slices/socket.slice";
import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { SOCKET_BASEURI } from "../../utils/helper";
import Toaster from "../../components/ui/Toaster";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../common/Sidebar";
import Header from "../common/Header";
import Footer from "../common/Footer";
import { io } from "socket.io-client";
import { Box } from "@mui/material";
import { showToast } from "../../redux/guest-slices/other.slice";
import { useFirebaseNotifications } from "../../hooks/useFirebaseNotifications";
import { useSessionTimeout } from "../../hooks/useSessionTimeout";

import { playReceiveMessageSound } from "../../utils/sound.utils";

function Layout() {
  useFirebaseNotifications();
  useSessionTimeout();
  const socket = useRef(null);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [active, setActive] = useState(false);

  const sidebarActive = () => {
    setActive(true);
  };
  const sidebarDeActive = () => {
    setActive(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [pathname]);

  const { specialist } = useSelector((state) => state.authSlice);
  const currentChat = useSelector((state) => state.socketSlice.chat);

  useEffect(() => {
    const userId = specialist?._id || specialist?.userId;
    if (userId) {
      dispatch(fetchAppointmentUnreadCount());
      dispatch(fetchMessagesUnreadCount());
    }
  }, [dispatch, specialist]);

  useEffect(() => {
    socket.current = io.connect(SOCKET_BASEURI);
    if (!socket.current) return;
    dispatch(setSocket(socket.current));
    // eslint-disable-next-line
  }, [socket.current]);

  useEffect(() => {
    if (!socket.current) return;
    const userId = specialist?._id || specialist?.userId;
    const specDocId = specialist?.specialist?._id;

    if (userId) {
      socket.current.emit("join-user", userId);
      socket.current.emit("get-chats", { userId, currentPage: 1, limit: 20 });
    }
    if (specDocId && specDocId.toString() !== userId?.toString()) {
      socket.current.emit("join-user", specDocId);
    }
  }, [socket.current, specialist]);

  useEffect(() => {
    if (!socket.current) return;

    const handleChatsList = ({ data }) => {
      if (Array.isArray(data?.records)) {
        const total = data.records.reduce((acc, r) => acc + (r.unreadCount || r.unread || 0), 0);
        dispatch(setMessagesUnreadCount(total));
      }
    };

    socket.current.on("chats-list", handleChatsList);
    return () => {
      socket.current?.off("chats-list", handleChatsList);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!socket.current) return;

    const handleNewMessageNotification = (data) => {
      playReceiveMessageSound();

      if (data.chat?.type === "appointment") {
        dispatch(fetchAppointmentUnreadCount());
      } else {
        if (data.unreadCount !== undefined) {
          dispatch(setMessagesUnreadCount(data.unreadCount));
        } else {
          dispatch(incrementMessagesUnreadCount(1));
        }
      }

      if (currentChat?._id === data.message.roomId) {
        return;
      }

      const userType = "specialist";
      const messageText = data.message.type === "text" ? data.message.text : "sent a file";
      const senderName = data.chat?.user?.fullName || "Someone";
      
      const toastMsg = data.chat?.type === "appointment"
        ? `Appointment Message from ${senderName}: "${messageText}"`
        : `Message from ${senderName}: "${messageText}"`;

      dispatch(showToast({
        message: toastMsg,
        type: "info",
        duration: 5000,
        onClickPath: data.chat?.type === "appointment" 
          ? `/${userType}/profile/appointment-messages` 
          : `/${userType}/profile/messages`,
        onClickState: data.chat
      }));
    };

    socket.current.on("new-message-notification", handleNewMessageNotification);
    return () => {
      if (socket.current) {
        socket.current.off("new-message-notification", handleNewMessageNotification);
      }
    };
  }, [socket.current, currentChat, specialist, dispatch]);

  return (
    <Box
      sx={{
        display: "grid",
        minHeight: "100vh",
        gridTemplateRows: "auto auto 1fr",
        overflowX: "hidden",
        maxWidth: "100%",
      }}
    >
      <Sidebar active={active} sidebarDeActive={sidebarDeActive} />
      <Header sidebarActive={sidebarActive} />
      <Box
        component="main"
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <Outlet context={{ active }} />
      </Box>
      <Footer />
      <Toaster />
      <DialogContainer />
    </Box>
  );
}

export default Layout;
